import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Paper, TextField, Typography } from '@mui/material';
import InventoryDTO from '@src/adapters/dto/inventory/InventoryDTO';
import useFindInventoriesLazy from '@src/adapters/hooks/inventory/useFindInventories';
import { debounce } from 'lodash';
import InventoryList from '@src/components/inventory/InventoryList';
import InventoryDetails from '@src/components/inventory/InventoryDetails';

function InventoryEdit() {
  const { execute: find } = useFindInventoriesLazy();
  const [selected, setSelected] = useState<InventoryDTO>();
  const [updated, setUpdated] = useState<InventoryDTO>();
  const [filter, setFilter] = useState('');
  const [inventories, setInventories] = useState<InventoryDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [recordCount, setRecordCount] = useState(0);

  const handleCancel = () => {
    setSelected(undefined);
  };

  const handleSaved = (inventory: InventoryDTO) => {
    setUpdated(inventory);
    setSelected(undefined);
  };

  const handleDeleted = (id: string) => {
    fetchInventories();
    setSelected(undefined);
  };

  const handleSelectProcedure = (inventory: InventoryDTO) => {
    setSelected(inventory);
  };

  const fetchInventories = useCallback(() => {
    find({
      filter,
      pageSize: rowsPerPage,
      currentPage: currentPage,
    }).then((result) => {
      if (result.inventories) {
        setInventories(
          result.inventories.sort((a, b) => a.name.localeCompare(b.name))
        );
      }
      if (result.recordCount) {
        setRecordCount(result.recordCount);
      }
    });
  }, [find, filter, rowsPerPage, currentPage]);

  const debouncedFindInventories = useMemo(
    () =>
      debounce(() => {
        fetchInventories();
      }, 500),
    [fetchInventories]
  );

  useEffect(() => {
    if (!filter) {
      fetchInventories();
    } else if (filter !== '') {
      setCurrentPage(0);
      debouncedFindInventories();
    }
    return () => {
      debouncedFindInventories.cancel();
    };
  }, [filter, debouncedFindInventories, fetchInventories]);

  useEffect(() => {
    if (updated) {
      setInventories((prev) =>
        prev.find((c) => c.id === updated.id)
          ? prev.map((c) => (c.id === updated.id ? updated : c))
          : [...prev, updated]
      );
    }
  }, [updated]);

  return (
    <>
      <Box sx={{ padding: '0 20px 20px 20px', display: 'flex' }}>
        <Typography variant="h4">Inventories</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Paper
          elevation={8}
          sx={{
            justifyContent: 'space-between',
            height: '80vh',
            width: '60%',
            overflow: 'auto',
          }}>
          <Box sx={(theme) => ({ padding: theme.spacing(2) })}>
            <TextField
              label="Filter (name or alias)"
              variant="outlined"
              onChange={(e) => setFilter(e.target.value)}
            />
          </Box>
          <Box>
            <InventoryList
              select={handleSelectProcedure}
              selected={selected}
              inventories={inventories}
              currentPage={currentPage}
              recordCount={recordCount}
              rowsPerPage={rowsPerPage}
              setCurrentPage={setCurrentPage}
              setRowsPerPage={setRowsPerPage}
            />
          </Box>
        </Paper>
        <Box sx={{ width: '38%' }}>
          {selected && (
            <InventoryDetails
              inventory={selected}
              cancel={handleCancel}
              saved={handleSaved}
              deleted={handleDeleted}
            />
          )}
        </Box>
      </Box>
    </>
  );
}

export default InventoryEdit;
